import { Check, Copy, X } from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';
import { toast } from 'sonner';

export function GiftCardSuccessModal({ isOpen, onClose, vouchers = [] }) {
    const [copiedStates, setCopiedStates] = useState({});

    if (!isOpen) return null;

    const copyToClipboard = (text, type, index) => {
        navigator.clipboard.writeText(text);
        const key = `${type}-${index}`;
        setCopiedStates((prev) => ({ ...prev, [key]: true }));
        setTimeout(() => setCopiedStates((prev) => ({ ...prev, [key]: false })), 2000);
        toast.success(`${type === 'code' ? 'Voucher Code' : 'PIN'} copied to clipboard`);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="relative w-full max-w-md bg-white rounded-[2.5rem] p-8 shadow-2xl animate-in zoom-in-95 duration-300">
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 hover:bg-surface rounded-full transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-10 h-10 text-success" />
                    </div>
                    <h2 className="font-heading text-3xl text-secondary mb-2">Order Successful!</h2>
                    <p className="text-muted-foreground font-body">Your gift card has been generated.</p>
                </div>

                <div className="space-y-4 max-h-[40vh] overflow-y-auto px-1">
                    {vouchers.length === 0 ? (
                        <div className="p-6 bg-surface rounded-3xl border border-dashed border-primary/30 text-center text-muted-foreground font-body">
                            Check My Orders
                        </div>
                    ) : (
                        vouchers.map((voucher, index) => (
                            <div key={index} className="p-6 bg-surface rounded-3xl border border-dashed border-primary/30">
                                <div className="mb-4">
                                    <span className="text-xs font-body font-bold text-primary uppercase tracking-widest block mb-2">Voucher Code {vouchers.length > 1 ? `#${index + 1}` : ''}</span>
                                    <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-gray-100">
                                        <code className="text-lg font-mono font-bold text-secondary tracking-wider">{voucher.code}</code>
                                        <button
                                            onClick={() => copyToClipboard(voucher.code, 'code', index)}
                                            className="p-2 hover:bg-surface rounded-xl transition-colors text-primary"
                                        >
                                            {copiedStates[`code-${index}`] ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {voucher.pin && (
                                    <div>
                                        <span className="text-xs font-body font-bold text-primary uppercase tracking-widest block mb-2">Security PIN</span>
                                        <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-gray-100">
                                            <code className="text-lg font-mono font-bold text-secondary tracking-wider">{voucher.pin}</code>
                                            <button
                                                onClick={() => copyToClipboard(voucher.pin, 'pin', index)}
                                                className="p-2 hover:bg-surface rounded-xl transition-colors text-primary"
                                            >
                                                {copiedStates[`pin-${index}`] ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="mt-8 space-y-3">
                    <p className="text-xs text-center text-muted-foreground font-body">
                        A copy of this voucher has also been sent to your email.
                    </p>
                    <Button
                        onClick={onClose}
                        className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-white font-body font-semibold"
                    >
                        Done
                    </Button>
                </div>
            </div>
        </div>
    );
}
